/**
 * Local calendar arithmetic and labels.
 *
 * The one place in this app that deals in real dates. The gantt's axis is
 * relative on purpose — "+3m" means about three months out, not a calendar
 * boundary — but a calendar of who is about is useless without Tuesday the
 * 23rd, so this is where months, weeks and DST live.
 *
 * Everything is in the VIEWER's timezone. The wire carries unix ms, so two
 * people in different zones see the same span land on their own local clock,
 * which is the right answer for "can we talk at four".
 *
 * Day arithmetic goes through Date, never through adding 86_400_000: on the two
 * days a year the clocks move, a day is not 24 hours, and adding milliseconds
 * lands an hour into the wrong day and shifts the whole week.
 */

/** Midnight at the start of the day `ms` falls in, locally. */
export function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** The same clock time `days` later, honouring any DST change in between. */
export function addDays(ms: number, days: number): number {
  const d = new Date(ms);
  d.setDate(d.getDate() + days);
  return d.getTime();
}

/**
 * How many local midnights lie between two instants: 0 on the same day, 1 the
 * next. Both ends are taken to their midnights first, so the only thing divided
 * is a whole number of days, 23 or 25 hours long across a clock change —
 * rounding is what absorbs that hour, not a way of guessing the day.
 */
export function daysBetween(fromMs: number, toMs: number): number {
  return Math.round((startOfDay(toMs) - startOfDay(fromMs)) / 86_400_000);
}

/** `count` consecutive days from `startMs`, each as its own half-open span. */
export function days(startMs: number, count: number): { startMs: number; endMs: number }[] {
  const out: { startMs: number; endMs: number }[] = [];
  let cursor = startOfDay(startMs);
  for (let i = 0; i < count; i++) {
    const next = addDays(cursor, 1);
    out.push({ startMs: cursor, endMs: next });
    cursor = next;
  }
  return out;
}

export const isToday = (ms: number): boolean => startOfDay(ms) === startOfDay(Date.now());

export const isWeekend = (ms: number): boolean => [0, 6].includes(new Date(ms).getDay());

const fmt = (opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat => new Intl.DateTimeFormat(undefined, opts);

const weekdayShort = fmt({ weekday: "short" });
const dayNumber = fmt({ day: "numeric" });
const timeOnly = fmt({ hour: "numeric", minute: "2-digit" });
const full = fmt({ weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });

export const weekdayLabel = (ms: number): string => weekdayShort.format(ms);
export const dayLabel = (ms: number): string => dayNumber.format(ms);
export const timeLabel = (ms: number): string => timeOnly.format(ms);
export const momentLabel = (ms: number): string => full.format(ms);

/** The hour label down the left of the grid: whatever "09:00" looks like here. */
export const hourLabel = (hour: number): string => {
  const d = new Date();
  d.setHours(hour, 0, 0, 0);
  return timeOnly.format(d);
};

const range = fmt({ day: "numeric", month: "short", year: "numeric" });

/**
 * A window as a heading: "14 – 20 Sep 2026", or whatever that is here.
 *
 * formatRange does the collapsing — one month named rather than two, one year
 * rather than two — per locale. Gluing two formatted dates together by hand
 * gets it wrong in every locale but one.
 *
 * The end is exclusive, so the heading names the last day IN the window.
 */
export function windowLabel(startMs: number, endExclusiveMs: number): string {
  return range.formatRange(new Date(startMs), new Date(addDays(endExclusiveMs, -1)));
}

/** How a span reads in a tooltip: "Mon 22 Sep, 09:00 → 17:00", or both dates when it crosses midnight. */
export function spanLabel(startMs: number, endMs: number): string {
  const sameDay = startOfDay(startMs) === startOfDay(endMs - 1);
  return sameDay
    ? `${momentLabel(startMs)} → ${timeLabel(endMs)}`
    : `${momentLabel(startMs)} → ${momentLabel(endMs)}`;
}

/**
 * A timestamp as an <input type="datetime-local"> value, and back.
 *
 * Deliberately NOT toISOString().slice(0, 16), which is UTC and would show
 * someone an hour they did not pick. The input has no timezone in it, and
 * `new Date("2026-09-21T09:00")` reads it back as local — the same clock the
 * grid is drawn on.
 */
export function toLocalInput(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** NaN for anything the input is not filled in with, which the validator then catches. */
export function fromLocalInput(text: string): number {
  return new Date(text).getTime();
}
